import { useCallback, useEffect, useState } from 'react';
import { AVAILABILITY_WS_EVENTS } from '../../../socket/constants';
import { subscribeAvailabilitySocketEvent } from '../../../socket/events';
import { AvailabilitySocketActivityPayload } from '../../../socket/types';
import { toStatusType } from '../../helpers';
import { extractEmployeeStatus } from '../helpers/statusHelpers';
import { StatusType } from '../../constants';
import useAvailabilityUserService from '../../useAvailabilityUserService';

const useAvailabilityUserStatus = (employeeId?: string | null) => {
  const [status, setStatus] = useState<StatusType | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { getAvailabilityUserById } = useAvailabilityUserService();
  const normalizedEmployeeId = employeeId?.trim() ?? '';

  const refreshStatus = useCallback(async () => {
    if (!normalizedEmployeeId) {
      setStatus(null);
      return;
    }

    try {
      setLoading(true);
      const response = await getAvailabilityUserById(normalizedEmployeeId);

      setStatus(extractEmployeeStatus(response));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`Failed to refresh interruption employee status id=${normalizedEmployeeId}: ${e}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedEmployeeId]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (!normalizedEmployeeId) {
      return undefined;
    }

    return subscribeAvailabilitySocketEvent<AvailabilitySocketActivityPayload>(
      AVAILABILITY_WS_EVENTS.ACTIVITY_UPDATE,
      payload => {
        const nextStatus = payload?.state ?? payload?.status;

        if (payload?.employeeId === normalizedEmployeeId && nextStatus) {
          setStatus(toStatusType(nextStatus));
        }
      },
    );
  }, [normalizedEmployeeId]);

  return {
    loading,
    status,
  };
};

export default useAvailabilityUserStatus;
